import { getErrorLocale, type ErrorLocale } from "./error-messages";

/** i18n for the order rating ("Avalie seu pedido"); follows `suggestions-messages.ts`. */
export type FeedbackMessageKey =
  | "nudge"
  | "rateCta"
  | "rated"
  | "sheetTitle"
  | "sheetSubtitle"
  | "star1"
  | "star2"
  | "star3"
  | "star4"
  | "star5"
  | "starAria"
  | "commentLabel"
  | "commentPlaceholder"
  | "submit"
  | "submitting"
  | "thanks"
  | "alreadyRated"
  | "notDelivered"
  | "windowExpired"
  | "failed"
  | "detailTitle";

const MESSAGES: Record<ErrorLocale, Record<FeedbackMessageKey, string>> = {
  "pt-BR": {
    nudge: "Avalie seu pedido",
    rateCta: "Avaliar",
    rated: "Você já avaliou",
    sheetTitle: "Como foi seu pedido?",
    sheetSubtitle: "Sua avaliação ajuda a loja a caprichar ainda mais.",
    star1: "Péssimo",
    star2: "Ruim",
    star3: "Ok",
    star4: "Bom",
    star5: "Excelente",
    starAria: "{n} de 5 estrelas",
    commentLabel: "Quer contar mais? (opcional)",
    commentPlaceholder: "O que você achou da comida, da entrega, do atendimento…",
    submit: "Enviar avaliação",
    submitting: "Enviando…",
    thanks: "Obrigado pela avaliação!",
    alreadyRated: "Este pedido já foi avaliado.",
    notDelivered: "Você poderá avaliar assim que o pedido for entregue.",
    windowExpired: "O prazo para avaliar este pedido já passou.",
    failed: "Não deu pra enviar sua avaliação. Tente de novo.",
    detailTitle: "Como foi seu pedido?",
  },
  "en-US": {
    nudge: "Rate your order",
    rateCta: "Rate",
    rated: "You already rated",
    sheetTitle: "How was your order?",
    sheetSubtitle: "Your rating helps the store do even better.",
    star1: "Terrible",
    star2: "Bad",
    star3: "Okay",
    star4: "Good",
    star5: "Excellent",
    starAria: "{n} of 5 stars",
    commentLabel: "Want to tell us more? (optional)",
    commentPlaceholder: "What did you think of the food, the delivery, the service…",
    submit: "Send rating",
    submitting: "Sending…",
    thanks: "Thanks for your rating!",
    alreadyRated: "This order was already rated.",
    notDelivered: "You can rate it as soon as the order is delivered.",
    windowExpired: "The time to rate this order is over.",
    failed: "We couldn't send your rating. Please try again.",
    detailTitle: "How was your order?",
  },
  es: {
    nudge: "Califica tu pedido",
    rateCta: "Calificar",
    rated: "Ya calificaste",
    sheetTitle: "¿Cómo estuvo tu pedido?",
    sheetSubtitle: "Tu calificación ayuda a la tienda a mejorar aún más.",
    star1: "Pésimo",
    star2: "Malo",
    star3: "Regular",
    star4: "Bueno",
    star5: "Excelente",
    starAria: "{n} de 5 estrellas",
    commentLabel: "¿Quieres contarnos más? (opcional)",
    commentPlaceholder: "Qué te pareció la comida, la entrega, la atención…",
    submit: "Enviar calificación",
    submitting: "Enviando…",
    thanks: "¡Gracias por tu calificación!",
    alreadyRated: "Este pedido ya fue calificado.",
    notDelivered: "Podrás calificarlo en cuanto el pedido sea entregado.",
    windowExpired: "El plazo para calificar este pedido ya terminó.",
    failed: "No pudimos enviar tu calificación. Inténtalo de nuevo.",
    detailTitle: "¿Cómo estuvo tu pedido?",
  },
};

/** Localized rating copy; `{n}` is replaced when given. */
export function feedbackMessage(key: FeedbackMessageKey, vars?: { n?: number }): string {
  const text = MESSAGES[getErrorLocale()][key];
  return vars?.n != null ? text.replace("{n}", String(vars.n)) : text;
}
